import React from 'react'
import { DotsThree, Star } from '@phosphor-icons/react'
import { Loader } from 'react-feather'
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Icon,
  IconButton
} from '@shopflo/ui'
import AppLogo from './AppLogo'
import DocumentationLinks from './DocumentationLinks'
import { ShopfloMonogram } from 'src/components/common/icons'
import { ItemStatusBadge } from './StatusBadge'
import WorksWithTags from './WorksWithTags'
import { AppDetailSidebarProps, AppMenuAction } from 'src/lib/types/appsIntegrations'
import { useLocale } from 'src/lib/hooks/useLocal'

const META_LABEL_CLASS = 'text-text-xs-medium text-zinc-500 w-20 flex-shrink-0'
const META_VALUE_CLASS = 'text-text-xs-medium text-zinc-700'

const MetaRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className='flex items-center gap-2 py-1'>
    <span className={META_LABEL_CLASS}>{label}</span>
    <span className={META_VALUE_CLASS}>{children}</span>
  </div>
)

const AppDetailSidebar: React.FC<AppDetailSidebarProps> = ({
  name,
  logoUrl,
  byShopflo,
  autoInstall = false,
  categoryLabel,
  price,
  markedUpPrice,
  numberOfBrandsUsing,
  rating,
  reviewCount,
  isActive,
  activating,
  menuActions,
  activationError,
  ctaLabel,
  onCtaClick,
  redirectUrl,
  onViewApp,
  onManage,
  worksWith,
  documentations,
  helpUrl,
  sidebarSlot
}) => {
  const { t } = useLocale()
  const priceSum = price ?? null
  const isFreePricing = priceSum === 0
  const hasMarkedPrice = markedUpPrice != null && markedUpPrice > 0
  const discountPct =
    !isFreePricing && hasMarkedPrice && priceSum != null && priceSum > 0 && markedUpPrice! > priceSum
      ? parseFloat((((markedUpPrice! - priceSum) / markedUpPrice!) * 100).toFixed(2))
      : null
  const showPricing = (!isActive || autoInstall) && (isFreePricing || (priceSum != null && priceSum > 0))

  // Extension actions first (e.g. Upsell's "View Plans"), then the standard ones every active app has.
  const finalMenuActions: AppMenuAction[] = [
    ...(menuActions ?? []),
    { key: 'manage', label: 'Manage', onClick: onManage },
    { key: 'uninstall', label: activating ? 'Uninstalling…' : 'Uninstall', onClick: onCtaClick, disabled: activating }
  ]
  const hasDocs = (documentations?.filter(d => d.url && d.name).length ?? 0) > 0 || Boolean(helpUrl?.trim())

  return (
    <>
      <div className='flex mb-2'>
        <AppLogo src={logoUrl} name={name} size='xl' />
      </div>

      <div className='flex flex-col mb-6'>
        <h2 className='text-text-lg-semibold text-zinc-900'>{name}</h2>
        <div className='flex flex-col gap-1'>
          <div className='flex items-center gap-1.5 flex-wrap'>
            {byShopflo && (
              <div className='flex items-center gap-1 text-zinc-500'>
                <span className='flex items-center justify-center w-3.5 h-3.5 rounded-sm bg-zinc-400 text-white p-0.5'>
                  <ShopfloMonogram className='w-full h-full' />
                </span>
                <span className='text-text-xs-medium'>{t('by_shopflo')}</span>
              </div>
            )}
            {byShopflo && categoryLabel && <span className='w-1 h-1 rounded-full bg-zinc-300 flex-shrink-0' />}
            {categoryLabel && <span className='text-text-xs-medium text-zinc-500'>{categoryLabel}</span>}
          </div>

          {(autoInstall || isActive) && (
            <div className='mt-1'>
              <ItemStatusBadge status={isActive ? 'active' : 'inactive'} autoInstall={autoInstall} />
            </div>
          )}
        </div>
      </div>

      {showPricing && (
        <div className='-mt-2 mb-4 flex flex-row items-start gap-2'>
          <div className='flex flex-col justify-center'>
            <span className='flex items-baseline gap-1.5'>
              {isFreePricing ? (
                <span className='text-text-md-semibold text-zinc-600'>Free</span>
              ) : (
                <>
                  <span className='text-text-md-semibold text-zinc-600'>₹{priceSum!.toLocaleString('en-IN')}</span>
                  <span className='text-text-sm-medium text-zinc-400'>/ month</span>
                </>
              )}
            </span>
            {discountPct != null && (
              <span className='text-text-xs-semibold text-emerald-600'>({discountPct}% off)</span>
            )}
          </div>
          {hasMarkedPrice &&
            (isFreePricing ? (
              <div className='flex h-6 flex-row items-center gap-2'>
                <span className='h-[3px] w-[3px] rounded-full bg-zinc-300' />
                <span className='text-text-sm-medium text-zinc-500 line-through opacity-60'>
                  ₹{markedUpPrice!.toLocaleString('en-IN')}/month
                </span>
              </div>
            ) : (
              <div className='flex h-6 flex-row items-center gap-2'>
                <span className='h-[3px] w-[3px] rounded-full bg-zinc-300' />
                <span className='text-text-sm-medium text-zinc-500 line-through opacity-60'>
                  ₹{markedUpPrice!.toLocaleString('en-IN')}
                </span>
              </div>
            ))}
        </div>
      )}

      {activationError && <p className='text-text-xs-regular text-red-600 -mb-4'>{activationError}</p>}

      {autoInstall ? (
        // Auto-installed apps can't be uninstalled by the merchant — only opened / managed.
        <div className='flex items-center gap-1.5 w-full'>
          <Button variant='outline' size='md' className='flex-1' onClick={onViewApp} disabled={!redirectUrl}>
            View App
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <IconButton
                variant='outline'
                size='md'
                IconComponent={DotsThree}
                iconProps={{ weight: 'bold' }}
                aria-label='More app actions'
              />
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end' sideOffset={4} className='p-1.5'>
              <DropdownMenuItem onClick={onManage}>Manage</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ) : isActive ? (
        <div className='flex items-center gap-1.5 w-full'>
          <Button variant='outline' size='md' className='flex-1' onClick={onViewApp} disabled={!redirectUrl}>
            View App
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <IconButton
                variant='outline'
                size='md'
                IconComponent={DotsThree}
                iconProps={{ weight: 'bold' }}
                aria-label='More app actions'
              />
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end' sideOffset={4} className='p-1.5'>
              {finalMenuActions.map(action => (
                <DropdownMenuItem
                  key={action.key}
                  onClick={action.onClick}
                  disabled={action.disabled}
                  className='text-text-sm-regular'
                >
                  {action.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ) : (
        <Button variant='primary' size='md' className='w-full' onClick={onCtaClick} disabled={activating}>
          {activating ? (
            <span className='flex items-center justify-center gap-1.5'>
              <Loader size={14} className='animate-spin' />
              Activating…
            </span>
          ) : (
            ctaLabel
          )}
        </Button>
      )}

      <div className='border-t border-zinc-200 my-5' />

      <div className='flex flex-col gap-4'>
        {sidebarSlot}
        {worksWith && worksWith.length > 0 && (
          <MetaRow label='Works with:'>
            <WorksWithTags items={worksWith} />
          </MetaRow>
        )}
        {numberOfBrandsUsing != null && numberOfBrandsUsing > 0 && (
          <MetaRow label='Used by:'>{numberOfBrandsUsing.toLocaleString('en-IN')}+ Brands</MetaRow>
        )}
        {categoryLabel && <MetaRow label='Helps with:'>{categoryLabel}</MetaRow>}
        {rating != null && rating > 0 && (
          <MetaRow label='Rating:'>
            <span className='flex items-center gap-1'>
              {rating.toFixed(1)}
              <Icon IconComponent={Star} size='xxs' iconProps={{ weight: 'fill' }} className='text-zinc-400' />
              {reviewCount != null && reviewCount > 0 && <span className='text-zinc-500'>({reviewCount} Reviews)</span>}
            </span>
          </MetaRow>
        )}
      </div>

      {hasDocs && <div className='border-t border-zinc-200 my-5' />}
      <DocumentationLinks documentations={documentations} helpUrl={helpUrl} />
    </>
  )
}

export default AppDetailSidebar
